import {
  Button,
  Checkbox,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Portal,
  Text,
} from '@chakra-ui/react';
import {
  ComplianceDocumentDefinition,
  ComplianceDocumentKind,
  ComplianceDocumentType,
} from '@texas/api/endpoints/metadata/complianceDocumentDefinitionsApi';
import { useTranslation } from 'react-i18next';
import { compareEnum } from '@texas/utils/helpers/enumHelpers';
import { Icons } from '../shared/Icons';

interface SupplierComplianceDocumentFilterProps {
  definitions: ComplianceDocumentDefinition[];
  isLoading: boolean;
  selectedDefinitionIds?: number[];
  onChange: (definitionIds?: number[]) => void;
}

export function SupplierComplianceDocumentFilter({
  definitions,
  isLoading,
  selectedDefinitionIds = [],
  onChange,
}: SupplierComplianceDocumentFilterProps) {
  const { t } = useTranslation();
  const selectedCount = selectedDefinitionIds.length;
  const scopedDefinitions = definitions.filter(({ type }) =>
    compareEnum(ComplianceDocumentType, type, ComplianceDocumentType.Scoped),
  );
  const certificates = scopedDefinitions.filter(({ kind }) =>
    compareEnum(
      ComplianceDocumentKind,
      kind,
      ComplianceDocumentKind.Certificate,
    ),
  );
  const reports = scopedDefinitions.filter(({ kind }) =>
    compareEnum(ComplianceDocumentKind, kind, ComplianceDocumentKind.Report),
  );

  const toggleDefinition = (definitionId: number) => {
    const definitionIds = selectedDefinitionIds.includes(definitionId)
      ? selectedDefinitionIds.filter((id) => id !== definitionId)
      : [...selectedDefinitionIds, definitionId];

    onChange(definitionIds.length > 0 ? definitionIds : undefined);
  };

  const renderDefinitions = (
    label: string,
    items: ComplianceDocumentDefinition[],
  ) => {
    if (items.length === 0) {
      return null;
    }

    return (
      <>
        <Text
          color="gray.500"
          fontSize="xs"
          fontWeight="semibold"
          px={3}
          py={2}
          textTransform="uppercase"
        >
          {label}
        </Text>
        {items.map((definition) => (
          <MenuItem
            key={definition.id}
            onClick={() => toggleDefinition(definition.id)}
          >
            <Checkbox
              isChecked={selectedDefinitionIds.includes(definition.id)}
              mr={2}
              pointerEvents="none"
            />
            {definition.name}
          </MenuItem>
        ))}
      </>
    );
  };

  return (
    <Menu closeOnSelect={false} isLazy={true}>
      <MenuButton
        as={Button}
        h="38px"
        isDisabled={isLoading || scopedDefinitions.length === 0}
        minW="150px"
        rightIcon={<Icons.ChevronDown boxSize={4} />}
        size="sm"
        variant="entityDirectoryFilter"
      >
        {t('suppliers.documents')}: {selectedCount || t('filter.all')}
      </MenuButton>
      <Portal>
        <MenuList maxH="360px" minW="280px" overflowY="auto" zIndex={20}>
          <MenuItem
            fontWeight={selectedCount === 0 ? 'semibold' : 'normal'}
            onClick={() => onChange(undefined)}
          >
            {t('suppliers.allDocuments')}
          </MenuItem>
          <MenuDivider />
          {renderDefinitions(t('suppliers.certificates'), certificates)}
          {certificates.length > 0 && reports.length > 0 && <MenuDivider />}
          {renderDefinitions(t('suppliers.reports'), reports)}
        </MenuList>
      </Portal>
    </Menu>
  );
}
